import type { User } from "@supabase/supabase-js";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { appEnvironment, hasSupabaseConfiguration } from "../lib/env";
import { supabase } from "../lib/supabase";
import {
  type CachedCurrentUser,
  clearCachedCurrentUser,
  readCachedCurrentUser,
  writeCachedCurrentUser,
} from "./authCache";

type CurrentUser = CachedCurrentUser;
type SessionUser = Pick<User, "id" | "email" | "user_metadata">;

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  configurationError: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  updatePassword(password: string): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const demoUser: CurrentUser = {
  id: "20000000-0000-4000-8000-000000000001",
  email: "tech@wroteitmyself.com",
  displayName: "Adam",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialCachedUser] = useState(() =>
    !appEnvironment.demoMode && hasSupabaseConfiguration ? readCachedCurrentUser() : null,
  );
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() =>
    initialCachedUser
      ? {
          id: initialCachedUser.id,
          ...(initialCachedUser.email ? { email: initialCachedUser.email } : {}),
          user_metadata: { display_name: initialCachedUser.displayName },
        }
      : null,
  );
  const [displayName, setDisplayName] = useState<string | null>(
    appEnvironment.demoMode ? "Adam" : (initialCachedUser?.displayName ?? null),
  );
  const [sessionLoading, setSessionLoading] = useState(
    !appEnvironment.demoMode && hasSupabaseConfiguration && !initialCachedUser,
  );

  useEffect(() => {
    if (!supabase || appEnvironment.demoMode) return;

    let active = true;
    const applySession = (nextUser: SessionUser | null) => {
      if (!active) return;
      if (!nextUser) {
        clearCachedCurrentUser();
        setSessionUser(null);
        setDisplayName(null);
        setSessionLoading(false);
        return;
      }

      const cached = readCachedCurrentUser();
      const nextDisplayName =
        cached?.id === nextUser.id ? cached.displayName : fallbackDisplayName(nextUser);
      setSessionUser(nextUser);
      setDisplayName(nextDisplayName);
      writeCachedCurrentUser({
        id: nextUser.id,
        email: nextUser.email,
        displayName: nextDisplayName,
      });
      setSessionLoading(false);
    };

    void supabase.auth
      .getSession()
      .then(({ data }) => applySession(data.session?.user ?? null))
      .catch(() => {
        if (active) setSessionLoading(false);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session?.user ?? null);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!supabase || appEnvironment.demoMode || !sessionUser) return;

    let active = true;
    const currentUser = sessionUser;
    void (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("user_id", currentUser.id)
          .single();
        if (!active) return;
        const profileName = data?.display_name?.trim();
        const nextDisplayName = profileName || fallbackDisplayName(currentUser);
        setDisplayName(nextDisplayName);
        writeCachedCurrentUser({
          id: currentUser.id,
          email: currentUser.email,
          displayName: nextDisplayName,
        });
      } catch {
        if (!active) return;
        const nextDisplayName = fallbackDisplayName(currentUser);
        setDisplayName(nextDisplayName);
        writeCachedCurrentUser({
          id: currentUser.id,
          email: currentUser.email,
          displayName: nextDisplayName,
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [sessionUser]);

  const user = useMemo<CurrentUser | null>(() => {
    if (appEnvironment.demoMode) return demoUser;
    if (!sessionUser || !displayName) return null;
    return {
      id: sessionUser.id,
      email: sessionUser.email,
      displayName,
    };
  }, [displayName, sessionUser]);

  const loading = sessionLoading || Boolean(sessionUser && !displayName);
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configurationError: !appEnvironment.demoMode && !hasSupabaseConfiguration,
      async signIn(email, password) {
        if (!supabase) throw new Error("Supabase has not been configured");
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      async signOut() {
        if (appEnvironment.demoMode) return;
        if (!supabase) return;
        const { error } = await supabase.auth.signOut({ scope: "global" });
        if (error) throw new Error(error.message);
        clearCachedCurrentUser();
        setSessionUser(null);
        setDisplayName(null);
      },
      async updatePassword(password) {
        if (password.length < 12) {
          throw new Error("Use at least 12 characters for the new password");
        }
        if (appEnvironment.demoMode) return;
        if (!supabase) throw new Error("Supabase has not been configured");
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw new Error(error.message);
      },
    }),
    [loading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

function fallbackDisplayName(user: Pick<User, "email" | "user_metadata">): string {
  const metadataName = user.user_metadata.display_name;
  if (typeof metadataName === "string" && metadataName.trim()) {
    return metadataName.trim();
  }
  return user.email?.split("@")[0] || "Team member";
}
